import { corsproxy_api_key } from "../config.js"
const FETCH_TIMEOUT_MS = 5000

class CorsProxyURL {
  constructor(site) {
    this.url = `https://corsproxy.io/?url=${encodeURIComponent(site)}`
    if (corsproxy_api_key) {
      this.url = `${this.url}&key=${corsproxy_api_key}`
    }
  }

  toString() {
    return this.url
  }
}
export class PwaManifestChecker extends HTMLElement {
  static tagName = "pwa-manifest-checker"

  #input
  #form
  #submitButton
  #manifestResult
  #manifestSummary
  #manifestUrl
  #manifestDetail
  #manifestContents

  #handleSubmit = (event) => {
    event.preventDefault()
    this.#update()
  }

  connectedCallback() {
    this.#input = this.querySelector("input[type='url']")
    this.#form = this.querySelector("form")
    this.#submitButton = this.querySelector("button[type='submit']")
    this.#manifestResult = this.querySelector("pwa-manifest-result")
    this.#manifestSummary = this.#manifestResult.querySelector("output[name='summary']")
    this.#manifestUrl = this.#manifestResult.querySelector("output[name='url']")
    this.#manifestDetail = this.#manifestResult.querySelector("output[name='detail']")
    this.#manifestContents = this.querySelector("pwa-manifest-contents")

    this.#form.addEventListener("submit", this.#handleSubmit)

    const params = new URLSearchParams(window.location.search)
    const initialUrl = params.get("url")
    if (initialUrl) {
      this.#input.value = initialUrl
      this.#update()
    }
  }

  #analyzeUrl(raw) {
    let parsed
    try {
      parsed = new URL(raw)
    } catch {
      if (!raw.includes(".")) {
        return { valid: false, isWebsite: false }
      }
      try {
        parsed = new URL(`https://${raw}`)
      } catch {
        return { valid: false, isWebsite: false }
      }
    }
    const isWebsite = parsed.protocol === "http:" || parsed.protocol === "https:"
    return { valid: true, isWebsite, parsed }
  }

  async #update() {
    const raw = this.#input.value

    this.#hideManifestResult()

    if (!raw.trim()) {
      history.replaceState(null, "", location.pathname)
      return
    }

    history.replaceState(null, "", "?" + new URLSearchParams({ url: raw }))

    const { valid, isWebsite, parsed } = this.#analyzeUrl(raw)

    if (!valid) {
      this.#manifestResult.setAttribute("status", "url-invalid")
      this.#manifestSummary.textContent = "Not a valid URL"
      this.#manifestUrl.textContent = ""
      this.#manifestDetail.textContent = ""
      return
    }

    if (!isWebsite) {
      const protocol = parsed.protocol.replace(/:$/, "")
      this.#manifestResult.setAttribute("status", "not-http")
      this.#manifestSummary.textContent = `Valid URL, but not an http/https website (protocol: ${protocol})`
      this.#manifestUrl.textContent = ""
      this.#manifestDetail.textContent = ""
      return
    }

    this.#manifestResult.setAttribute("status", "checking")
    this.#manifestSummary.textContent = ""
    this.#manifestUrl.textContent = ""
    this.#manifestDetail.textContent = ""
    this.#submitButton.disabled = true

    try {
      await this.#checkManifest(parsed)
    } finally {
      this.#submitButton.disabled = false
    }
  }

  async #checkManifest(parsedUrl) {
    const directPaths = ["/manifest.json", "/site.webmanifest"]
    for (const path of directPaths) {
      const manifestUrl = new URL(path, parsedUrl.href).href
      const data = await this.#tryFetchManifestDirect(manifestUrl)
      if (data !== null) {
        this.#manifestResult.setAttribute("status", "found")
        this.#manifestSummary.textContent = "Web manifest found"
        this.#manifestUrl.textContent = manifestUrl
        this.#manifestDetail.textContent = ""
        this.#manifestContents.show(data, parsedUrl.href)
        return
      }
    }

    await this.#checkManifestViaHtml(parsedUrl)
  }

  async #tryFetchManifestDirect(url) {
    const proxyUrl = new CorsProxyURL(url)
    try {
      const response = await fetch(proxyUrl.toString(), { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
      if (!response.ok) return null
      const text = await response.text()
      return JSON.parse(text)
    } catch {
      return null
    }
  }

  async #checkManifestViaHtml(parsedUrl) {
    const proxyUrl = new CorsProxyURL(parsedUrl.href)

    let html
    try {
      const response = await fetch(proxyUrl.toString(), { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
      if (!response.ok) {
        const statusText = response.statusText ? ` ${response.statusText}` : ""
        this.#displayManifestError(`HTTP ${response.status}${statusText}`)
        return
      }
      html = await response.text()
    } catch (e) {
      this.#manifestResult.setAttribute("status", "cant-reach-site")
      this.#manifestSummary.textContent = `Could not reach site: ${e.message}`
      this.#manifestUrl.textContent = ""
      this.#manifestDetail.textContent = ""
      return
    }

    const parsedDocument = new DOMParser().parseFromString(html, "text/html")
    const appleData = this.#extractApple(parsedDocument)
    this.#manifestContents.showApple(appleData, parsedUrl.href)

    const manifestHref = this.#findManifestLink(parsedDocument)
    await this.#displayManifestResult(manifestHref, parsedUrl.href)
  }

  #findManifestLink(parsedDocument) {
    const linkElement = parsedDocument.querySelector("link[rel~='manifest']")
    if (!linkElement) return null
    const href = linkElement.getAttribute("href")
    return href || null
  }

  #extractApple(parsedDocument) {
    const touchIcons = [...parsedDocument.querySelectorAll("link[rel='apple-touch-icon']")]
      .map(link => ({
        href:  link.getAttribute("href"),
        sizes: link.getAttribute("sizes"),
      }))

    const startupImages = [...parsedDocument.querySelectorAll("link[rel='apple-touch-startup-image']")]
      .map(link => ({
        href:  link.getAttribute("href"),
        media: link.getAttribute("media"),
      }))

    const metaContent = (name) => {
      const element = parsedDocument.querySelector(`meta[name='${name}']`)
      return element ? element.getAttribute("content") : null
    }

    return {
      touchIcons,
      startupImages,
      webAppCapable:  metaContent("apple-mobile-web-app-capable"),
      webAppTitle:    metaContent("apple-mobile-web-app-title"),
      statusBarStyle: metaContent("apple-mobile-web-app-status-bar-style"),
    }
  }

  async #displayManifestResult(href, baseUrl) {
    if (!href) {
      this.#manifestResult.setAttribute("status", "not-found")
      this.#manifestSummary.textContent = "No web manifest found"
      this.#manifestUrl.textContent = ""
      this.#manifestDetail.textContent = ""
      return
    }

    const resolvedUrl = new URL(href, baseUrl).href
    this.#manifestResult.setAttribute("status", "found")
    this.#manifestSummary.textContent = "Web manifest found"
    this.#manifestUrl.textContent = resolvedUrl
    this.#manifestDetail.textContent = ""

    await this.#fetchManifest(resolvedUrl, baseUrl)
  }

  async #fetchManifest(manifestUrl, baseUrl) {
    const proxyUrl = new CorsProxyURL(manifestUrl)
    const loadingStatuses = ["loading", "retrying", "retrying-last"]

    for (let attempt = 0; attempt < loadingStatuses.length; attempt++) {
      this.#manifestContents.showLoading(loadingStatuses[attempt])

      try {
        const response = await fetch(proxyUrl.toString(), { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
        if (!response.ok) {
          this.#manifestContents.showError(`Could not fetch manifest: HTTP ${response.status}`)
          return
        }
        const text = await response.text()
        const data = JSON.parse(text)
        this.#manifestContents.show(data, baseUrl)
        return
      } catch (error) {
        if (error instanceof SyntaxError) {
          this.#manifestContents.showError("Manifest is not valid JSON")
          return
        }
        const isTimeout = error instanceof DOMException && error.name === "TimeoutError"
        if (!isTimeout || attempt === loadingStatuses.length - 1) {
          this.#manifestContents.showError("Could not fetch manifest")
          return
        }
      }
    }
  }

  #displayManifestError(detail) {
    this.#manifestResult.setAttribute("status", "error")
    this.#manifestSummary.textContent = "Could not get site HTML"
    this.#manifestUrl.textContent = ""
    this.#manifestDetail.textContent = detail
  }

  #hideManifestResult() {
    this.#manifestResult.removeAttribute("status")
    this.#manifestSummary.textContent = ""
    this.#manifestUrl.textContent = ""
    this.#manifestDetail.textContent = ""
    this.#manifestContents.hide()
  }
}
